import { useState } from 'react';
import { useTranslation } from 'react-i18next';

interface UseDeleteWithConfirmationOptions<T> {
    onDelete: (id: string) => Promise<T>;
    confirmMessage: string;
    successMessage: (item: T) => string;
    onSuccess?: (item: T) => void;
    onError?: (error: unknown) => void;
}

export const useDeleteWithConfirmation = <T = unknown>({
    onDelete,
    confirmMessage,
    successMessage,
    onSuccess,
    onError,
}: UseDeleteWithConfirmationOptions<T>) => {
    const { t } = useTranslation();
    const [isDeleting, setIsDeleting] = useState(false);

    const confirmAndDelete = async (id: string): Promise<boolean> => {
        const isConfirmed = window.confirm(confirmMessage);
        if (!isConfirmed) {
            return false;
        }

        setIsDeleting(true);

        try {
            const deletedItem = await onDelete(id);
            onSuccess?.(deletedItem);
            return true;
        } catch (error) {
            onError?.(error);
            return false;
        } finally {
            setIsDeleting(false);
        }
    };

    return {
        confirmAndDelete,
        isDeleting,
    };
};
